import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Spin } from "antd";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { fetchSubscriptionByToken } from "../api/subscriptionsApi";
import { confirmPaymentRecord, createPaymentIntent } from "../api/paymentsApi";
import { STRIPE_PUBLISHABLE_KEY } from "../config/env";
import { cycleLabel, featureTexts, formatMoney, periodLabel, typeLabel } from "../utils/pricing";
import { toastError, toastSuccess } from "../utils/toast";
import "./css/pricing.css";

const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

function PaymentForm({ token, email, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/subscribe/success?token=${token}`,
          receipt_email: email || undefined,
        },
        redirect: "if_required",
      });

      if (error) {
        toastError(error.message || "Payment failed. Your subscription remains unpaid.");
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        await confirmPaymentRecord(token, paymentIntent.id);
        toastSuccess("Payment successful.");
        onPaid();
        return;
      }

      toastError("Payment was not completed. Your subscription remains unpaid.");
    } catch (err) {
      toastError(err?.response?.data?.message || "Payment could not be confirmed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="cl-pay-form">
      <PaymentElement />
      <button type="submit" className="btn btn-solid cl-checkout-submit" disabled={!stripe || submitting}>
        {submitting ? "Processing..." : "Submit payment"}
      </button>
    </form>
  );
}

export default function SubscribePaymentPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const data = await fetchSubscriptionByToken(token);
        if (!on) return;
        setSubscription(data);
        if (data?.payment_status === "paid") {
          navigate(`/subscribe/success?token=${token}`, { replace: true });
          return;
        }
        const intent = await createPaymentIntent(token);
        if (on) setClientSecret(intent?.client_secret || "");
      } catch (err) {
        toastError(err?.response?.data?.message || "Unable to load payment details.");
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => {
      on = false;
    };
  }, [token, navigate]);

  const options = useMemo(
    () => ({
      clientSecret,
      appearance: {
        theme: "stripe",
        variables: { colorPrimary: "#c9964a" },
      },
    }),
    [clientSecret]
  );

  if (loading) {
    return (
      <div className="cl-pricing-loading">
        <Spin />
      </div>
    );
  }

  if (!subscription) {
    return (
      <section className="cl-checkout">
        <div className="cl-container">
          <h1>Subscription not found</h1>
          <p>This payment link is invalid or has expired.</p>
        </div>
      </section>
    );
  }

  const customer = subscription.customer || {};

  return (
    <section className="cl-checkout">
      <div className="cl-container">
        <p className="cl-eyebrow">Plan and business details</p>
        <h1>Enter your payment details</h1>
        <div className="cl-checkout-grid">
          <aside className="cl-summary-card">
            <div className="cl-summary-head">
              <span className="cl-summary-icon">☰</span>
              <div>
                <h2>Package details</h2>
                <p>Review the selected plan and the amount due before paying.</p>
              </div>
            </div>
            <div className="cl-summary-plan">
              <div>
                <small>Selected plan</small>
                <strong>{subscription.package_name}</strong>
              </div>
              <div className="cl-summary-price">
                {formatMoney(subscription.price, subscription.currency)}
                <span>{periodLabel(subscription)}</span>
              </div>
            </div>
            <ul className="cl-meta-list">
              <li>
                <span>Subscription type</span>
                <strong>{typeLabel(subscription.subscription_type)}</strong>
              </li>
              <li>
                <span>Billing cycle</span>
                <strong>{cycleLabel(subscription.billing_cycle)}</strong>
              </li>
              <li>
                <span>Duration</span>
                <strong>
                  {subscription.duration_value} {subscription.duration_unit}
                </strong>
              </li>
            </ul>
            <ul className="cl-plan-features">
              {featureTexts(subscription).map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          </aside>

          <div className="cl-checkout-card">
            <div className="cl-summary-head">
              <span className="cl-summary-icon">💳</span>
              <div>
                <h2>Payment details</h2>
              </div>
            </div>
            <div className="cl-customer-box">
              <p>
                <strong>
                  {customer.first_name} {customer.last_name}
                </strong>
              </p>
              <p>{customer.email}</p>
              <p>{customer.phone}</p>
            </div>

            {!STRIPE_PUBLISHABLE_KEY || !stripePromise ? (
              <p className="cl-pay-warning">
                Stripe is not configured on this project yet. Your unpaid subscription is saved
                and can be completed once payment keys are added.
              </p>
            ) : !clientSecret ? (
              <p className="cl-pay-warning">
                
              </p>
            ) : (
              <Elements stripe={stripePromise} options={options}>
                <PaymentForm
                  token={token}
                  email={customer.email}
                  onPaid={() => navigate(`/subscribe/success?token=${token}`)}
                />
              </Elements>
            )}

            <Link to={`/subscribe/details?package_id=${subscription.package_id || ""}`} className="cl-pay-back">
              Previous
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
